import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, ButtonGroup, Badge, Spinner, Alert } from 'react-bootstrap';
import { 
  FaFileContract, 
  FaExclamationTriangle, 
  FaFileSignature, 
  FaCheckCircle,
  FaPlus
} from 'react-icons/fa';
import axiosInstance from '../../helpers/api';
import { useNavigate } from 'react-router-dom';

interface Transaction {
  id: string;
  file_number?: string;
  type: 'purchase' | 'listing' | 'rental' | 'referral';
  stage: string;
  client_name: string;
  client_email: string;
  client_id: string;
  compliance_status: string;
  created_at: string;
  documents_count?: number;
}

interface Metrics {
  active: number;
  pendingCompliance: number;
  documentsThisWeek: number;
  complianceScore: number;
}

interface TransactionDashboardProps {
  onTransactionSelect?: (transactionId: string) => void;
  onNewTransaction?: () => void;
}

const TransactionDashboard: React.FC<TransactionDashboardProps> = ({ onTransactionSelect, onNewTransaction }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [metrics, setMetrics] = useState<Metrics>({
    active: 0,
    pendingCompliance: 0,
    documentsThisWeek: 0,
    complianceScore: 0
  });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch metrics
      const metricsRes = await axiosInstance.get('/transactions/metrics');
      setMetrics(metricsRes.data);
      
      // Fetch transactions
      const params = filter !== 'all' ? { stage: filter } : {};
      const transRes = await axiosInstance.get('/transactions', { params });
      setTransactions(transRes.data.transactions || []);
      
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      pre_contract: 'info',
      showing: 'primary',
      under_contract: 'warning',
      closing: 'success',
      completed: 'secondary',
      cancelled: 'danger'
    };
    return colors[stage] || 'secondary';
  };

  const getComplianceColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'warning',
      submitted: 'info',
      approved: 'success',
      rejected: 'danger'
    };
    return colors[status] || 'secondary';
  };

  const formatStage = (stage: string) => {
    return stage.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2 text-muted">Loading transactions...</p>
      </div>
    );
  }

  return (
    <div className="transaction-dashboard">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Transaction Dashboard</h4>
        <Button 
          variant="primary" 
          onClick={onNewTransaction || (() => navigate('/agent-portal/transactions/new'))}
        >
          <FaPlus className="me-2" />
          New Transaction
        </Button>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Metric Cards */}
      <Row className="mb-4">
        <Col md={3} sm={6} className="mb-3">
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <FaFileContract size={30} className="text-primary mb-2" />
              <h3 className="mb-1">{metrics.active}</h3>
              <p className="text-muted mb-0">Active Transactions</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <FaExclamationTriangle size={30} className="text-warning mb-2" />
              <h3 className="mb-1">{metrics.pendingCompliance}</h3>
              <p className="text-muted mb-0">Pending Compliance</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <FaFileSignature size={30} className="text-info mb-2" />
              <h3 className="mb-1">{metrics.documentsThisWeek}</h3>
              <p className="text-muted mb-0">Documents This Week</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="text-center h-100 shadow-sm">
            <Card.Body>
              <FaCheckCircle size={30} className="text-success mb-2" />
              <h3 className="mb-1">{metrics.complianceScore}%</h3>
              <p className="text-muted mb-0">Compliance Score</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Transactions List */}
      <Card className="shadow-sm">
        <Card.Header className="bg-white">
          <Row className="align-items-center">
            <Col md={6}>
              <h5 className="mb-0">Active Transactions</h5>
            </Col>
            <Col md={6} className="text-md-end mt-2 mt-md-0">
              <ButtonGroup size="sm">
                <Button 
                  variant={filter === 'all' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('all')}
                >
                  All
                </Button>
                <Button 
                  variant={filter === 'pre_contract' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('pre_contract')}
                >
                  Pre-Contract
                </Button>
                <Button 
                  variant={filter === 'under_contract' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('under_contract')}
                >
                  Under Contract
                </Button>
                <Button 
                  variant={filter === 'closing' ? 'primary' : 'outline-primary'}
                  onClick={() => setFilter('closing')}
                >
                  Closing
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {transactions.length === 0 ? (
            <div className="text-center py-5">
              <FaFileContract size={50} className="text-muted mb-3" />
              <p className="text-muted">
                {filter === 'all' 
                  ? 'No transactions found. Create your first transaction to get started!'
                  : `No ${formatStage(filter)} transactions found.`
                }
              </p>
              {filter !== 'all' && (
                <Button 
                  variant="link" 
                  onClick={() => setFilter('all')}
                  className="text-decoration-none"
                >
                  View all transactions
                </Button>
              )}
            </div>
          ) : (
            <div className="transaction-list">
              {transactions.map(transaction => (
                <Card 
                  key={transaction.id} 
                  className="mb-3 border cursor-pointer transaction-card"
                  onClick={() => onTransactionSelect ? onTransactionSelect(transaction.id) : navigate(`/agent-portal/transactions/${transaction.id}`)}
                >
                  <Card.Body>
                    <Row className="align-items-center">
                      <Col md={4}>
                        <h6 className="mb-1 text-dark">{transaction.client_name}</h6>
                        <small className="text-muted d-block">
                          {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)} Transaction
                        </small>
                        {transaction.file_number && (
                          <small className="text-primary fw-bold">
                            File #: {transaction.file_number}
                          </small>
                        )}
                      </Col>
                      <Col md={2} className="text-center my-2 my-md-0">
                        <Badge bg={getStageColor(transaction.stage)} className="px-3 py-2">
                          {formatStage(transaction.stage)}
                        </Badge>
                      </Col>
                      <Col md={3} className="text-center my-2 my-md-0">
                        <Badge bg={getComplianceColor(transaction.compliance_status)} className="px-3 py-2">
                          Compliance: {transaction.compliance_status}
                        </Badge>
                      </Col>
                      <Col md={3} className="text-md-end">
                        <small className="text-muted">
                          Created: {formatDate(transaction.created_at)}
                        </small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      <style>{`
        .transaction-card {
          transition: all 0.2s ease;
          border-color: #e0e0e0 !important;
        }
        .transaction-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          border-color: #007bff !important;
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .transaction-dashboard h3 {
          font-weight: 600;
          color: #2c3e50;
        }
        .transaction-dashboard .badge {
          font-weight: 500;
          font-size: 0.85rem;
        }
      `}</style>
    </div>
  );
};

export default TransactionDashboard;